@defer {
<div class="relative flex flex-col min-h-screen">

	@if (isLoading()) {
	} @else if (noSeason()) {
		<div class="flex justify-center items-center flex-1 pt-28 pb-16">
			<peps-no-content [text]="'page.battlepeps.no-season.text' | translate"></peps-no-content>
		</div>
	} @else if (current(); as data) {

		<!-- ── Hero ────────────────────────────────────────────────── -->
		<div class="relative pt-28 pb-16 px-8 lg:px-20 overflow-hidden">

			<!-- Dot grid -->
			<div class="absolute inset-0 pointer-events-none opacity-[0.03]"
				style="background-image: radial-gradient(circle, #EEC932 1px, transparent 1px); background-size: 28px 28px;">
			</div>
			<!-- Left golden glow -->
			<div class="absolute inset-0 pointer-events-none"
				style="background: linear-gradient(100deg, rgba(238,201,50,0.07) 0%, transparent 55%);">
			</div>
			<!-- Cross decoration -->
			<img src="assets/images/cross.avif" alt=""
				class="absolute left-0 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none select-none h-64 w-auto object-contain">

			<div class="relative flex flex-col gap-10">

				<!-- Title block -->
				<div class="flex flex-col md:flex-row md:items-end justify-between gap-6">
					<div class="flex flex-row gap-5 items-center" data-tour="header">
						<div class="w-1 self-stretch bg-peps-gold shrink-0"></div>
						<div class="flex flex-col gap-1">
							<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-gold tracking-[0.35em]">
								{{ data.season.year }} · {{ data.season.name }}
							</span>
							<h1 class="text-4xl sm:text-6xl md:text-9xl uppercase font-dinpro text-peps-dark-200 leading-none">
								{{ 'page.battlepeps.title' | translate }}
							</h1>
						</div>
					</div>

					<div class="flex flex-row items-center gap-3 self-start md:self-auto shrink-0">
						<!-- Rejouer le tutoriel -->
						<button
							type="button"
							class="flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-peps-dark-700 border border-peps-dark-500 hover:border-peps-gold text-peps-dark-200/70 hover:text-peps-gold shrink-0 cursor-pointer transition-colors"
							[pepsTooltip]="'page.battlepeps.tutorial.replay' | translate"
							pepsTooltipSize="14px"
							(click)="startTour()"
						>
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-5 lg:size-6">
								<path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519a3 3 0 1 1 3.61 4.63c-.83.667-1.489 1.622-1.489 2.851m0 3h.008M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
							</svg>
						</button>

						<!-- Timer widget -->
						<div class="flex flex-row items-center gap-3 bg-peps-dark-700 border border-peps-dark-500 px-5 py-3">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4 lg:size-5 text-peps-gold shrink-0">
								<path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clip-rule="evenodd" />
							</svg>
							<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-dark-200/50 tracking-wider">
								{{ 'page.battlepeps.season.ends-in' | translate }}
							</span>
							<span class="text-lg lg:text-xl font-dinpro-medium text-peps-dark-200 tabular-nums">
								{{ (data.season.endDate | countdown) ?? ('page.battlepeps.season.ended' | translate) }}
							</span>
						</div>
					</div>
				</div>

				@if (data.progress; as progress) {
					<div class="flex flex-col gap-6 max-w-2xl">

						<!-- Tier / XP progress -->
						<div class="flex flex-col gap-3" data-tour="xp-progress">
							<div class="flex flex-row justify-between items-center text-base lg:text-lg uppercase font-dinpro-medium">
								<span class="text-peps-dark-200/40 tracking-wider">
									{{ 'page.battlepeps.progress.tier' | translate }} {{ progress.currentTier }}
								</span>
								<span class="text-peps-dark-200/70 tabular-nums">
									{{ progress.xpCurrent }}&thinsp;/&thinsp;{{ progress.xpForNextTier }}&nbsp;XP
								</span>
							</div>
							<div class="relative w-full h-2 bg-peps-dark-500">
								<div
									class="absolute inset-y-0 left-0 bg-peps-gold transition-all duration-700"
									[style.width.%]="tierProgressPercent()"
								></div>
								@if (tierProgressPercent() > 0 && tierProgressPercent() < 100) {
									<div
										class="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-peps-gold rounded-full -translate-x-1/2 shadow-lg"
										[style.left.%]="tierProgressPercent()"
										style="box-shadow: 0 0 8px rgba(238,201,50,0.8);"
									></div>
								}
							</div>
						</div>

						<!-- Rang : bonus sur l'XP gagnée, pas une monnaie à part -->
						<div class="flex flex-col gap-3" data-tour="rank">
							<div class="flex flex-row justify-between items-center text-base lg:text-lg uppercase font-dinpro-medium flex-wrap gap-2">
								<span class="text-peps-dark-200/40 tracking-wider">{{ 'page.battlepeps.progress.rank' | translate }}</span>
								<span class="flex items-center gap-2">
									<span
										class="text-peps-gold border-b border-dotted border-peps-gold/50 cursor-help"
										[pepsTooltip]="progress.currentRank.bonusPercentage > 0
											? ('page.battlepeps.progress.rank-bonus-tooltip' | translate:{ rank: progress.currentRank.label, bonus: progress.currentRank.bonusPercentage })
											: progress.currentRank.label"
										pepsTooltipSize="14px"
									>
										{{ progress.currentRank.label }}
									</span>
									@if (progress.currentRank.bonusPercentage > 0) {
										<span class="text-peps-dark-200/40 normal-case tracking-normal">(+{{ progress.currentRank.bonusPercentage }}% XP)</span>
									}
								</span>
							</div>

							@if (ranks().length > 0) {
								<div class="overflow-x-auto lg:overflow-visible no-scrollbar -mx-1 px-1 lg:mx-0 lg:px-0">
									<div class="flex flex-row items-center w-max lg:w-full">
										@for (rank of ranks(); track rank.code) {
											<div
												class="w-9 h-9 lg:w-16 lg:h-16 font-notosans rounded-full flex items-center justify-center border-2 shrink-0 transition-colors cursor-help"
												[class.border-peps-gold]="rank.displayOrder <= progress.currentRank.displayOrder"
												[class.border-peps-dark-500]="rank.displayOrder > progress.currentRank.displayOrder"
												[pepsTooltip]="rank.bonusPercentage > 0
													? ('page.battlepeps.progress.rank-bonus-tooltip' | translate:{ rank: rank.label, bonus: rank.bonusPercentage })
													: rank.label"
												pepsTooltipSize="12px"
											>
												<img
													[src]="rankIcon(rank.code)"
													alt=""
													class="w-6 h-6 lg:w-10 lg:h-10 object-contain"
													[class.opacity-30]="rank.displayOrder > progress.currentRank.displayOrder"
												>
											</div>
											@if (!$last) {
												<div class="flex-1 w-8 min-w-8 lg:w-auto lg:min-w-0 h-1.5 bg-peps-dark-400 relative overflow-hidden mx-0.5">
													<div
														class="absolute inset-y-0 left-0 bg-peps-gold transition-all duration-700"
														[style.width.%]="rank.displayOrder < progress.currentRank.displayOrder ? 100 : (rank.displayOrder === progress.currentRank.displayOrder ? rankProgressPercent() : 0)"
													></div>
												</div>
											}
										}
									</div>
								</div>
							}
						</div>
					</div>
				}
				@if(globalQuests().length > 0) {
					<div class="flex flex-col gap-3 w-full" data-tour="global-quests">
						<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-dark-200/40 tracking-[0.3em]">
							{{ 'page.battlepeps.global-quests.title' | translate }}
						</span>
						<div class="relative">
							<div #questSlider class="flex flex-row overflow-x-auto snap-x snap-mandatory no-scrollbar scroll-smooth" (scroll)="onQuestScroll(questSlider)">
							@for (quest of globalQuests(); track quest.id) {
								<div class="snap-center shrink-0 w-full flex flex-col gap-3 bg-peps-dark-700 border border-peps-dark-500 px-6 py-5">
									<div class="flex flex-col md:flex-row items-start justify-between gap-4">
										<h4 class="uppercase font-dinpro-medium text-xl text-peps-dark-200">{{ questTitle(quest) }}</h4>
										<span class="shrink-0 text-peps-gold font-dinpro text-lg lg:text-xl uppercase"><span class="text-white">
											{{'page.battlepeps.global.reward' | translate }}</span> {{ quest.rewardType }} {{ quest.rewardItemName }} x{{ quest.rewardAmount }}</span>
									</div>
									@if (questDescription(quest)) {
										<p class="text-lg lg:text-xl text-peps-dark-200/55 font-notosans leading-relaxed">{{ questDescription(quest) }}</p>
									}
									<a [href]="quest.link" target="_blank" class="text-center w-full bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 uppercase font-dinpro-medium text-lg lg:text-xl px-6 py-3 cursor-pointer transition-colors">
										{{ 'page.battlepeps.global-quests.button' | translate }}
									</a>
								</div>
							}
							</div>
							@if (globalQuests().length > 1) {
								<button type="button" aria-label="Previous" class="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-peps-dark-700/90 border border-peps-dark-500 hover:border-peps-gold text-peps-dark-200 hover:text-peps-gold flex items-center justify-center cursor-pointer transition-colors disabled:opacity-30 disabled:pointer-events-none" [disabled]="activeQuest() === 0" (click)="prevQuest(questSlider)">
									<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
								</button>
							}
							@if (globalQuests().length > 1) {
								<button type="button" aria-label="Next" class="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-peps-dark-700/90 border border-peps-dark-500 hover:border-peps-gold text-peps-dark-200 hover:text-peps-gold flex items-center justify-center cursor-pointer transition-colors disabled:opacity-30 disabled:pointer-events-none" [disabled]="activeQuest() === globalQuests().length - 1" (click)="nextQuest(questSlider)">
									<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
								</button>
							}
						</div>
						@if (globalQuests().length > 1) {
							<div class="flex flex-row justify-center gap-2">
								@for (quest of globalQuests(); track quest.id; let i = $index) {
									<button type="button" class="w-2.5 h-2.5 rounded-full cursor-pointer transition-colors" [class.bg-peps-gold]="i === activeQuest()" [class.bg-peps-dark-500]="i !== activeQuest()" (click)="goToQuest(questSlider, i)"></button>
								}
							</div>
						}
					</div>
				}
			</div>
		</div>

		<div class="w-full h-px bg-peps-dark-500"></div>

		@if (data.progress; as progress) {

			<!-- ── Reward track (paliers, style battle pass) ────────── -->
			<div class="px-8 lg:px-20 py-14 flex flex-col gap-8" data-tour="rewards">
				<div class="flex flex-row items-center justify-between gap-4 flex-wrap">
					<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-dark-200/40 tracking-[0.3em]">
						{{ 'page.battlepeps.rewards.title' | translate }}
					</span>
					@if (hasPendingRewards()) {
						<button
							class="flex items-center gap-2 bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 px-5 py-2.5 uppercase font-dinpro-medium text-base lg:text-lg tracking-wider cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
							[disabled]="claimingAll()"
							(click)="claimAll()"
						>
							@if (claimingAll()) {
								<span class="inline-block w-3.5 h-3.5 border-2 border-peps-dark-700 border-t-transparent rounded-full animate-spin"></span>
							} @else {
								{{ 'page.battlepeps.rewards.claim-all' | translate }} ({{ pendingRewards().length }})
							}
						</button>
					}
				</div>

				@if (sortedTiers().length === 0) {
					<p class="text-lg lg:text-xl font-notosans text-peps-dark-200/40">{{ 'page.battlepeps.rewards.empty' | translate }}</p>
				} @else {
					<div class="relative">

						<!-- Left fade + arrow -->
						<div class="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-peps-dark-700 to-transparent pointer-events-none z-10"></div>
						<button
							class="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-peps-dark-700 border border-peps-dark-500 hover:border-peps-gold text-peps-dark-200 hover:text-peps-gold flex items-center justify-center cursor-pointer transition-colors"
							(click)="tierTrack.scrollBy({ left: -tierTrack.clientWidth * 0.8, behavior: 'smooth' })"
						>
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-5 lg:size-6">
								<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
							</svg>
						</button>

						<div #tierTrack class="overflow-x-auto no-scrollbar scroll-smooth pb-2">
							<div class="flex flex-row items-start min-w-max md:min-w-0 px-16 gap-2">

								@for (tier of sortedTiers(); track tier.id) {
									<!-- Node -->
									<div [id]="'tier-' + tier.tierNumber" class="flex flex-col items-center gap-2 lg:gap-3 w-[110px] lg:w-[352px] shrink-0 scroll-mx-16">

										<!-- "Actuel" pointer -->
										<div class="h-4 lg:h-6 flex items-end justify-center">
											@if (tier.tierNumber === progress.currentTier) {
												<span class="text-[10px] lg:text-lg uppercase font-dinpro-medium text-peps-gold tracking-wider whitespace-nowrap">
													{{ 'page.battlepeps.rewards.current' | translate }}
												</span>
											}
										</div>

										<span
											class="text-base lg:text-4xl font-dinpro-medium tracking-wider transition-colors duration-300"
											[class.text-peps-gold]="tier.status === 'PENDING_CLAIM'"
											[class.text-peps-dark-200/50]="tier.status === 'CLAIMED'"
											[class.text-peps-dark-200/25]="tier.status === null"
										>
											{{ tier.tierNumber }}
										</span>

										<div
											class="relative w-[90px] h-[90px] lg:w-[300px] lg:h-[300px] rounded-full flex items-center justify-center border-2 transition-all duration-500"
											[class.border-peps-gold]="tier.status === 'PENDING_CLAIM'"
											[class.bg-peps-dark-800]="tier.status === 'PENDING_CLAIM'"
											[class.border-peps-dark-500]="tier.status !== 'PENDING_CLAIM'"
											[class.bg-peps-dark-700]="tier.status !== 'PENDING_CLAIM'"
											[ngClass]="tier.tierNumber === progress.currentTier ? 'ring-4 ring-peps-gold/25' : ''"
											[style.box-shadow]="tier.status === 'PENDING_CLAIM' ? '0 0 24px rgba(238,201,50,0.35), inset 0 0 14px rgba(238,201,50,0.06)' : 'none'"
										>
											<img
												[src]="tier.rewardType === 'COINS' ? 'assets/images/tas_pepscoins.png' : 'assets/images/booster/booster_extension_0.png'"
												alt=""
												class="w-12 h-12 lg:w-44 lg:h-44 object-contain transition-opacity duration-500"
												[class.opacity-15]="tier.status === null"
											>

											@if (tier.status === 'CLAIMED') {
												<div class="absolute -top-1 -right-1 w-5 h-5 lg:w-14 lg:h-14 rounded-full bg-peps-gold flex items-center justify-center shadow">
													<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-3 lg:size-9 text-peps-dark-700">
														<path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z" clip-rule="evenodd" />
													</svg>
												</div>
											} @else if (tier.status === null) {
												<div class="absolute inset-0 flex items-center justify-center">
													<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6 lg:size-14 text-peps-dark-200/25">
														<path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
													</svg>
												</div>
											}

											<!-- Pépites qui éclatent au moment de la récupération -->
											@if (justClaimedTiers().has(tier.tierNumber)) {
												<div class="tier-claim-coins">
													<img class="tier-claim-coin" style="--dx: 130px; --dy: 0px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: 105px; --dy: 76px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: 40px; --dy: 124px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: -40px; --dy: 124px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: -105px; --dy: 76px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: -130px; --dy: 0px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: -105px; --dy: -76px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: -40px; --dy: -124px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: 40px; --dy: -124px" src="assets/emotes/pepite.png" alt="">
													<img class="tier-claim-coin" style="--dx: 105px; --dy: -76px" src="assets/emotes/pepite.png" alt="">
												</div>
											}
										</div>

										<span
											class="text-[11px] lg:text-2xl font-notosans text-center leading-tight tabular-nums"
											[class.text-peps-dark-200/70]="tier.status !== null"
											[class.text-peps-dark-200/25]="tier.status === null"
										>
											@if (tier.rewardType === 'COINS') {
												+{{ tier.rewardValue }}
											} @else {
												x{{ tier.rewardValue }}
											}
											{{ ('page.battlepeps.rewards.type.' + tier.rewardType.toLowerCase()) | translate }}
										</span>

										@if (tier.status === 'PENDING_CLAIM') {
											<button
												class="w-full bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 uppercase font-dinpro-medium text-[10px] lg:text-xl px-1.5 py-1.5 lg:px-3 lg:py-3 cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
												[disabled]="claimingTier() !== null || claimingAll()"
												(click)="claimTier(tier)"
											>
												@if (claimingTier() === tier.tierNumber) {
													<span class="inline-block w-4 h-4 border-2 border-peps-dark-700 border-t-transparent rounded-full animate-spin"></span>
												} @else {
													{{ 'page.battlepeps.rewards.claim' | translate }}
												}
											</button>
										} @else if (tier.status === 'CLAIMED' && tier.claimedAt) {
											<span class="text-[10px] lg:text-lg uppercase text-peps-dark-200/30 font-notosans">
												{{ tier.claimedAt | localizedDate:'d MMM' }}
											</span>
										} @else {
											<span class="text-[10px] lg:text-lg uppercase text-peps-dark-200/20 font-notosans tabular-nums">{{ tier.thresholdXp }} XP</span>
										}
									</div>

									<!-- Connector between nodes : plein si le palier suivant est atteint, partiel sur le tronçon en cours -->
									@if (!$last) {
										<div class="flex items-start mt-[6.25rem] lg:mt-[14.875rem] flex-1 min-w-3 lg:min-w-16 px-1 lg:px-2">
											<div class="w-full h-1.5 bg-peps-dark-400 relative overflow-hidden">
												<div
													class="absolute inset-y-0 left-0 bg-peps-gold transition-all duration-700"
													[style.width.%]="tier.status !== null ? 100 : (tier.tierNumber === progress.currentTier ? tierProgressPercent() : 0)"
												></div>
											</div>
										</div>
									}
								}

							</div>
						</div>

						<!-- Right fade + arrow -->
						<div class="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-peps-dark-700 to-transparent pointer-events-none z-10"></div>
						<button
							class="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-peps-dark-700 border border-peps-dark-500 hover:border-peps-gold text-peps-dark-200 hover:text-peps-gold flex items-center justify-center cursor-pointer transition-colors"
							(click)="tierTrack.scrollBy({ left: tierTrack.clientWidth * 0.8, behavior: 'smooth' })"
						>
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-5 lg:size-6">
								<path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
							</svg>
						</button>

					</div>
				}
			</div>

			<div class="w-full h-px bg-peps-dark-500"></div>

		} @else {

			<!-- ── Invité : pas de progression sans compte ──────────── -->
			<div class="px-8 lg:px-20 py-14 flex flex-col md:flex-row items-center justify-between gap-6" data-tour="login-cta">
				<div class="flex flex-col gap-1 text-center md:text-left">
					<h3 class="text-3xl lg:text-4xl uppercase font-dinpro text-peps-dark-200">{{ 'page.battlepeps.login-required.title' | translate }}</h3>
					<p class="text-lg lg:text-xl text-peps-dark-200/60 font-notosans">{{ 'page.battlepeps.login-required.text' | translate }}</p>
				</div>
				<button
					class="shrink-0 bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 uppercase font-dinpro-medium text-lg lg:text-xl px-6 py-3 cursor-pointer transition-colors"
					(click)="loginWithTwitch()"
				>
					{{ 'page.battlepeps.login-required.cta' | translate }}
				</button>
			</div>

			<div class="w-full h-px bg-peps-dark-500"></div>

		}

		@if (authService.isAuthenticated()) {
			<div class="w-full h-px bg-peps-dark-500"></div>

			<!-- ── Quêtes à réaliser ─────────────────────────────────── -->
			<div class="px-8 lg:px-20 py-14 flex flex-col gap-6" data-tour="quests">
				<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-dark-200/40 tracking-[0.3em]">
					{{ 'page.battlepeps.quests.title' | translate }}
				</span>
				@if (todoQuests().length === 0) {
					<p class="text-lg lg:text-xl font-notosans text-peps-dark-200/40">{{ 'page.battlepeps.quests.empty' | translate }}</p>
				} @else {
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						@for (quest of todoQuests(); track quest.id) {
							<div class="flex flex-col gap-3 bg-peps-dark-700 border border-peps-dark-500 px-6 py-5">
								<div class="flex items-start justify-between gap-4">
									<h4 class="uppercase font-dinpro-medium text-peps-dark-200">{{ questTitle(quest) }}</h4>
									@if (!quest.tipRoleTiers?.length) {
										<span class="shrink-0 text-peps-gold font-dinpro text-lg lg:text-xl tabular-nums">+{{ quest.xpReward }} XP</span>
									}
								</div>
								@if (questDescription(quest)) {
									<p class="text-lg lg:text-xl text-peps-dark-200/55 font-notosans leading-relaxed">{{ questDescription(quest) }}</p>
								}
								@if (quest.tipRoleTiers?.length) {
									<details class="group border border-peps-dark-500 bg-peps-dark-800/40">
										<summary class="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden">
											<span class="text-sm lg:text-base uppercase tracking-wider text-peps-dark-200/40 font-dinpro-medium">
												{{ 'page.battlepeps.quests.tip-role-tiers-title' | translate }}
											</span>
											<svg class="shrink-0 w-4 h-4 text-peps-dark-200/40 transition-transform duration-200 group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
												<polyline points="6 9 12 15 18 9"></polyline>
											</svg>
										</summary>
										<div class="flex flex-col gap-1.5 px-3 pb-2.5 pt-0.5">
											@for (tier of quest.tipRoleTiers; track tier.discordRoleId) {
												<div class="flex items-center justify-between gap-3">
													<span class="text-base lg:text-lg text-peps-dark-200/70 font-notosans truncate">{{ tipRoleTierLabel(tier) }}</span>
													<span class="shrink-0 text-peps-gold font-dinpro tabular-nums">+{{ tier.xp }} XP</span>
												</div>
											}
										</div>
									</details>
								}
								<div class="flex items-center gap-2 flex-wrap">
									<span class="text-sm lg:text-base uppercase tracking-wider px-2 py-1 border border-peps-dark-500 text-peps-dark-200/45">
										{{ questScopeLabelKey(quest.scope) | translate }}
									</span>
									@if (quest.completionStatus === 'REJECTED') {
										<span class="text-sm lg:text-base uppercase tracking-wider px-2 py-1 border border-peps-red/40 text-peps-red">
											{{ 'page.battlepeps.quests.status.rejected' | translate }}
										</span>
									}
									@if (quest.completionStatus === 'VALIDATED') {
										<span class="text-sm lg:text-base uppercase tracking-wider px-2 py-1 border border-peps-gold/40 text-peps-gold">
											{{ 'page.battlepeps.quests.status.validated' | translate }}
										</span>
									}
								</div>

								<div class="mt-1">
									@if (quest.verificationMethod === 'MANUAL_ADMIN') {
										@if (canDeclare(quest)) {
											@if (quest.requiresImage) {
												<input #imageInput type="file" accept="image/jpeg,image/png,image/jpg" class="hidden"
													(change)="declareQuestWithImage(quest, imageInput)" />
												<button
													class="bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 uppercase font-dinpro-medium text-base lg:text-lg px-4 py-2 cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
													[disabled]="declaringQuestId() !== null"
													(click)="imageInput.click()"
												>
													@if (declaringQuestId() === quest.id) {
														<span class="inline-block w-3 h-3 border-2 border-peps-dark-700 border-t-transparent rounded-full animate-spin"></span>
													} @else {
														{{ 'page.battlepeps.quests.upload-proof' | translate }}
													}
												</button>
											} @else {
												<button
													class="bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 uppercase font-dinpro-medium text-base lg:text-lg px-4 py-2 cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
													[disabled]="declaringQuestId() !== null"
													(click)="declareQuest(quest)"
												>
													@if (declaringQuestId() === quest.id) {
														<span class="inline-block w-3 h-3 border-2 border-peps-dark-700 border-t-transparent rounded-full animate-spin"></span>
													} @else {
														{{ 'page.battlepeps.quests.declare' | translate }}
													}
												</button>
											}
										}
									} @else if (quest.verificationMethod === 'EXTERNAL_API') {
										@if (canVerify(quest)) {
											<button
												class="bg-peps-gold hover:bg-peps-yellow text-peps-dark-700 uppercase font-dinpro-medium text-base lg:text-lg px-4 py-2 cursor-pointer transition-colors disabled:opacity-50 disabled:pointer-events-none"
												[disabled]="verifyingQuestId() !== null"
												(click)="verifyQuest(quest)"
											>
												@if (verifyingQuestId() === quest.id) {
													<span class="inline-block w-3 h-3 border-2 border-peps-dark-700 border-t-transparent rounded-full animate-spin"></span>
												} @else {
													{{ 'page.battlepeps.quests.verify' | translate }}
												}
											</button>
										}
									} @else {
										<span class="text-base lg:text-lg text-peps-dark-200/40 italic font-notosans">{{ 'page.battlepeps.quests.auto-verification' | translate }}</span>
									}
								</div>
							</div>
						}
					</div>
				}
			</div>

			<!-- ── Quêtes en attente de validation ───────────────────── -->
			@if (pendingQuests().length > 0) {
				<div class="w-full h-px bg-peps-dark-500"></div>
				<div class="px-8 lg:px-20 py-14 flex flex-col gap-6">
					<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-dark-200/40 tracking-[0.3em]">
						{{ 'page.battlepeps.quests.pending-title' | translate }}
					</span>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						@for (quest of pendingQuests(); track quest.id) {
							<div class="flex flex-col gap-3 bg-peps-dark-700 border border-peps-gold/30 px-6 py-5">
								<div class="flex items-start justify-between gap-4">
									<h4 class="uppercase font-dinpro-medium text-peps-dark-200">{{ questTitle(quest) }}</h4>
									@if (!quest.tipRoleTiers?.length) {
										<span class="shrink-0 text-peps-gold font-dinpro text-lg lg:text-xl tabular-nums">+{{ quest.xpReward }} XP</span>
									}
								</div>
								@if (questDescription(quest)) {
									<p class="text-lg lg:text-xl text-peps-dark-200/55 font-notosans leading-relaxed">{{ questDescription(quest) }}</p>
								}
								@if (quest.tipRoleTiers?.length) {
									<details class="group border border-peps-dark-500 bg-peps-dark-800/40">
										<summary class="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden">
											<span class="text-sm lg:text-base uppercase tracking-wider text-peps-dark-200/40 font-dinpro-medium">
												{{ 'page.battlepeps.quests.tip-role-tiers-title' | translate }}
											</span>
											<svg class="shrink-0 w-4 h-4 text-peps-dark-200/40 transition-transform duration-200 group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
												<polyline points="6 9 12 15 18 9"></polyline>
											</svg>
										</summary>
										<div class="flex flex-col gap-1.5 px-3 pb-2.5 pt-0.5">
											@for (tier of quest.tipRoleTiers; track tier.discordRoleId) {
												<div class="flex items-center justify-between gap-3">
													<span class="text-base lg:text-lg text-peps-dark-200/70 font-notosans truncate">{{ tipRoleTierLabel(tier) }}</span>
													<span class="shrink-0 text-peps-gold font-dinpro tabular-nums">+{{ tier.xp }} XP</span>
												</div>
											}
										</div>
									</details>
								}
								<span class="w-fit text-sm lg:text-base uppercase tracking-wider px-2 py-1 border border-peps-gold/40 text-peps-gold">
									{{ 'page.battlepeps.quests.status.pending' | translate }}
								</span>
							</div>
						}
					</div>
				</div>
			}

			<!-- ── Historique des quêtes réalisées ───────────────────── -->
			<div class="w-full h-px bg-peps-dark-500"></div>
			<div class="px-8 lg:px-20 py-14 flex flex-col gap-6">
				<span class="text-base lg:text-lg uppercase font-dinpro-medium text-peps-dark-200/40 tracking-[0.3em]">
					{{ 'page.battlepeps.quests.history.title' | translate }}
				</span>
				@if (completedQuests().length === 0) {
					<p class="text-lg lg:text-xl font-notosans text-peps-dark-200/40">{{ 'page.battlepeps.quests.history.empty' | translate }}</p>
				} @else {
					<div class="flex flex-col gap-3">
						@for (c of completedQuests(); track c.completionId) {
							<div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 bg-peps-dark-700 border border-peps-dark-500 px-6 py-4">
								<div class="flex flex-col gap-0.5 min-w-0">
									<span class="uppercase font-dinpro-medium text-peps-dark-200 text-lg lg:text-xl">{{ questTitle(c) }}</span>
									@if (questDescription(c)) {
										<span class="text-base lg:text-lg text-peps-dark-200/45 font-notosans">{{ questDescription(c) }}</span>
									}
								</div>
								<div class="flex items-center gap-4 shrink-0">
									<span class="text-peps-gold font-dinpro text-lg lg:text-xl tabular-nums">+{{ c.xpAwarded }} XP</span>
									<span class="text-sm lg:text-base uppercase text-peps-dark-200/40 font-notosans tabular-nums">{{ c.completedAt | localizedDate:'d MMM yyyy' }}</span>
								</div>
							</div>
						}
					</div>
				}
			</div>
		}

		<!-- ── Classement ────────────────────────────────────────── -->
		<div
			class="lg:fixed lg:top-0 lg:right-0 lg:h-full lg:z-50 lg:bg-peps-dark-800 lg:border-l lg:border-peps-dark-500 lg:transition-transform lg:duration-300 "
			[class.lg:translate-x-full]="!isLeaderboardOpen()"
			[class.lg:translate-x-0]="isLeaderboardOpen()"
		>
			<button
				class="p-2 text-lg bg-peps-dark-700 border border-peps-dark-500 hover:border-peps-gold text-peps-gold [writing-mode:vertical-rl] [text-orientation:upright] uppercase hover:text-peps-gold flex items-center justify-center cursor-pointer transition-colors right-full absolute top-1/2 -translate-y-1/2"
				(click)="toggleLeaderboard()"
			>
				{{ 'page.leaderboard.title' | translate }}
			</button>
			<div class="px-8 py-14 lg:p-0 flex flex-col gap-8 lg:bg-peps-dark-800" data-tour="leaderboard">
			<span class="text-base uppercase font-dinpro-medium text-peps-dark-200/40 tracking-[0.3em] lg:text-center lg:py-4 lg:text-xl">
				{{ 'page.leaderboard.title' | translate }}
			</span>
				@if (leaderboard().length > 0) {
					<div class="flex flex-col w-full gap-4 lg:overflow-y-auto lg:h-[calc(100vh-80px)] lg:custom-scrollbar">
						<div class="w-full overflow-x-auto custom-scrollbar">
							<table class="min-w-[300px] w-full max-w-full text-lg lg:text-xl font-notosans border-separate border-spacing-y-2">
								<thead>
								<tr class="text-peps-dark-200/60 uppercase text-base lg:text-lg">
									<td class="px-2 py-1.5 lg:px-6 lg:py-3 text-left">{{ 'page.leaderboard.rank' | translate }}</td>
									<td class="px-2 py-1.5 lg:px-6 lg:py-3 text-left min-w-[140px]">{{ 'page.leaderboard.username' | translate }}</td>
									<td class="px-4 py-2 text-right">{{ 'page.battlepeps.leaderboard.xp' | translate }}</td>
								</tr>
								</thead>
								<tbody>
									@for (entry of leaderboard(); track entry.userId) {
										<tr class="w-full bg-peps-dark-600 text-peps-dark-200/90">
											<td class="px-2 py-1.5 lg:px-6 lg:py-3">{{ entry.position }}</td>
											<td class="px-2 py-1.5 lg:px-6 lg:py-3 text-left">
												<a
													[routerLink]="['/profil', entry.username]"
													class="flex items-center gap-2 hover:underline w-fit"
													[class.text-peps-gold]="currentUsername() === entry.username"
													[style.text-shadow]="currentUsername() === entry.username ? '0 0 4px rgba(238,201,50,0.7)' : 'none'"
												>
													@if (entry.rankCode) {
														<img [src]="rankIcon(entry.rankCode)" [alt]="rankLabel(entry.rankCode)" [pepsTooltip]="rankLabel(entry.rankCode)" class="w-5 h-5 object-contain shrink-0" />
													}
													<span>{{ entry.username }}</span>
												</a>
											</td>
											<td class="px-4 py-3 text-right font-medium">
												<span class="text-peps-gold text-2xl lg:text-3xl tabular-nums">{{ entry.xpCurrent }}</span>
												<span class="text-peps-dark-200/60 uppercase text-base lg:text-lg">XP</span>
											</td>
										</tr>
									}
								</tbody>
							</table>
						</div>
						<peps-page-selector
							[page]="leaderboardPage()"
							[totalPages]="leaderboardTotalPages()"
							(pageChange)="onLeaderboardPageChange($event)"
							[goToTop]="false"
						></peps-page-selector>
					</div>
				}
			</div>
		</div>

	}

</div>
}
